import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/queryClient";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequirePermission } from "@/components/RequirePermission";
import { LoginPage } from "@/routes/LoginPage";
import { ChangePasswordPage } from "@/routes/ChangePasswordPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { PlaceholderPage } from "@/routes/PlaceholderPage";
import { ClientesListPage } from "@/features/clientes/ClientesListPage";
import { ClienteDetailPage } from "@/features/clientes/ClienteDetailPage";
import { PlanesListPage } from "@/features/planes/PlanesListPage";
import { RedesListPage } from "@/features/redes/RedesListPage";
import { RoutersListPage } from "@/features/routers/RoutersListPage";
import { JobsListPage } from "@/features/jobs/JobsListPage";
import { AjustesPage } from "@/features/ajustes/AjustesPage";
import { AjustesSectionPage } from "@/features/ajustes/AjustesSectionPage";
import { SitiosListPage } from "@/features/sitios/SitiosListPage";
import { ContratosListPage } from "@/features/contratos/ContratosListPage";
import { ContratoDetailPage } from "@/features/contratos/ContratoDetailPage";
import { AuditGlobalPage } from "@/features/audit/AuditGlobalPage";
import { FacturasListPage } from "@/features/facturas/FacturasListPage";
import { FacturaDetailPage } from "@/features/facturas/FacturaDetailPage";
import { VpnPage } from "@/features/vpn/VpnPage";
import { UsuariosListPage } from "@/features/usuarios/UsuariosListPage";
import { CobranzaPage } from "@/features/cobranza/CobranzaPage";
import { PagosListPage } from "@/features/pagos/PagosListPage";
import { PromesasListPage } from "@/features/promesas/PromesasListPage";
import { SeguridadPage } from "@/features/seguridad/SeguridadPage";
import { ImportacionPage } from "@/features/importacion/ImportacionPage";
import { TicketsListPage } from "@/features/tickets/TicketsListPage";
import { TicketDetailPage } from "@/features/tickets/TicketDetailPage";
import { ProspectosListPage } from "@/features/prospectos/ProspectosListPage";
import { AnalisisPage } from "@/features/analisis/AnalisisPage";
import { MapaPage } from "@/features/ubicaciones/MapaPage";
import { EquiposPage } from "@/features/equipos/EquiposPage";
import { ProspectoDetailPage } from "@/features/prospectos/ProspectoDetailPage";
import { CapturaPublicaPage } from "@/features/prospectos/CapturaPublicaPage";
import { IconSprite } from "@/components/icons/sprite";
import { useEffect } from "react";
import { useBranding } from "@/lib/branding";
import { setTz } from "@/lib/tz";
import { resincronizarPush } from "@/lib/push";
import { useAuthStore } from "@/lib/auth";

/** rc130 — carga el branding público una vez y fija la zona horaria del ISP
 *  para los helpers de formato (lib/utils.ts). No renderiza nada. */
function BrandingBoot() {
  const { data } = useBranding();
  useEffect(() => { setTz(data?.timezone); }, [data?.timezone]);
  // rc135 — si este navegador ya tiene push activo, re-asociar la suscripción
  // al usuario que inició sesión (por si el teléfono lo comparte otro operador).
  const token = useAuthStore((s) => s.accessToken);
  useEffect(() => { if (token) resincronizarPush(); }, [token]);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingBoot />
      {/* rc56: sprite global de iconos outline. Una sola instancia, las
          referencias via <Icon name="…" /> hacen <use href="#i-…"/>. */}
      <IconSprite />
      <BrowserRouter>
        <Routes>
          {/* Públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          {/* Captura pública de ubicación del prospecto (link con token, sin auth) */}
          <Route path="/captura/:token" element={<CapturaPublicaPage />} />

          {/* Privadas */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={
              <RequirePermission perm="dashboard.read"><DashboardPage /></RequirePermission>
            } />
            {/* rc40: denyRoles=["cajero"] en todas las vistas admin globales.
                Cajero ya tiene perms read heredados pero NO debe entrar a las
                listas globales (ver clientes, facturas, pagos, etc. completos)
                — su flujo es exclusivamente vía Dashboard + Pago Rápido. */}
            <Route path="clientes" element={
              <RequirePermission perm="clientes.read" denyRoles={["cajero"]}><ClientesListPage /></RequirePermission>
            } />
            <Route path="clientes/:id" element={
              <RequirePermission perm="clientes.read" denyRoles={["cajero"]}><ClienteDetailPage /></RequirePermission>
            } />
            <Route path="planes" element={
              <RequirePermission perm="planes.read" denyRoles={["cajero"]}><PlanesListPage /></RequirePermission>
            } />
            <Route path="redes" element={
              <RequirePermission perm="redes.read"><RedesListPage /></RequirePermission>
            } />
            <Route path="routers" element={
              <RequirePermission perm="routers.read"><RoutersListPage /></RequirePermission>
            } />
            <Route path="vpn" element={
              <RequirePermission perm="vpn.read"><VpnPage /></RequirePermission>
            } />
            <Route path="sitios" element={
              <RequirePermission perm="clientes.read" denyRoles={["cajero"]}><SitiosListPage /></RequirePermission>
            } />
            {/* rc102 — mapa de clientes/contratos */}
            <Route path="mapa" element={
              <RequirePermission perm="contratos.read" denyRoles={["cajero"]}><MapaPage /></RequirePermission>
            } />
            {/* rc104 — inventario de comodatos */}
            <Route path="equipos" element={
              <RequirePermission perm="contratos.read" denyRoles={["cajero"]}><EquiposPage /></RequirePermission>
            } />
            <Route path="contratos" element={
              <RequirePermission perm="contratos.read" denyRoles={["cajero"]}><ContratosListPage /></RequirePermission>
            } />
            <Route path="contratos/:id" element={
              <RequirePermission perm="contratos.read" denyRoles={["cajero"]}><ContratoDetailPage /></RequirePermission>
            } />
            <Route path="tickets" element={
              <RequirePermission perm="tickets.read" denyRoles={["cajero"]}><TicketsListPage /></RequirePermission>
            } />
            <Route path="tickets/:id" element={
              <RequirePermission perm="tickets.read" denyRoles={["cajero"]}><TicketDetailPage /></RequirePermission>
            } />
            <Route path="prospectos" element={
              <RequirePermission perm="prospectos.read" denyRoles={["cajero"]}><ProspectosListPage /></RequirePermission>
            } />
            <Route path="prospectos/:id" element={
              <RequirePermission perm="prospectos.read" denyRoles={["cajero"]}><ProspectoDetailPage /></RequirePermission>
            } />
            <Route path="jobs" element={
              <RequirePermission perm="routers.read"><JobsListPage /></RequirePermission>
            } />
            <Route path="auditoria" element={
              <RequirePermission perm="usuarios.read"><AuditGlobalPage /></RequirePermission>
            } />
            <Route path="facturas" element={
              <RequirePermission perm="facturas.read" denyRoles={["cajero"]}><FacturasListPage /></RequirePermission>
            } />
            <Route path="facturas/:id" element={
              <RequirePermission perm="facturas.read" denyRoles={["cajero"]}><FacturaDetailPage /></RequirePermission>
            } />
            <Route path="cobranza" element={
              <RequirePermission perm="cobranza.read" denyRoles={["cajero"]}><CobranzaPage /></RequirePermission>
            } />
            <Route path="pagos" element={
              <RequirePermission perm="pagos.read" denyRoles={["cajero"]}><PagosListPage /></RequirePermission>
            } />
            <Route path="promesas" element={
              <RequirePermission perm="cobranza.read" denyRoles={["cajero"]}><PromesasListPage /></RequirePermission>
            } />
            {/* rc98 — módulo Análisis (números agregados del negocio; cajero jamás) */}
            <Route path="analisis" element={
              <RequirePermission perm="analisis.read" denyRoles={["cajero"]}><AnalisisPage /></RequirePermission>
            } />
            <Route path="usuarios" element={
              <RequirePermission perm="usuarios.read"><UsuariosListPage /></RequirePermission>
            } />
            <Route path="ajustes" element={
              <RequirePermission perm="usuarios.read"><AjustesPage /></RequirePermission>
            } />
            <Route path="ajustes/:section" element={
              <RequirePermission perm="usuarios.read"><AjustesSectionPage /></RequirePermission>
            } />
            <Route path="seguridad" element={<SeguridadPage />} />
            <Route path="importacion" element={
              <RequirePermission perm="usuarios.write"><ImportacionPage /></RequirePermission>
            } />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      {/* rc135.1 — en la app instalada en iPhone los toasts salían bajo la barra de
          estado: offset con el área segura (0 en navegador normal). */}
      <Toaster
        position="top-right"
        richColors
        theme="system"
        offset="calc(env(safe-area-inset-top) + 16px)"
        mobileOffset="calc(env(safe-area-inset-top) + 12px)"
      />
    </QueryClientProvider>
  );
}
